/**
 * CheatMenuComponents - React components for the cheat menu
 * Modernized to React 18+ with TypeScript
 * 
 * Note: React is loaded globally via CDN, so we use the global React object
 * We import types only (not runtime) to avoid bundling React
 */

import type { Component, ChangeEvent } from 'react';
import { Variable } from '../models/Variable';
import { CustomProperty } from '../models/CustomProperty';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Forward declarations
declare const cheatFreezes: {
    variables: { [key: string]: boolean };
    playerProperties: { [key: string]: boolean };
    customProperties?: { [entityId: string]: { [propertyName: string]: boolean } };
};
declare const TheGame: any;

function valueForVariable(variable: Variable): string | number | undefined {
    if (variable.vartype === "VT_NUMBER") {
        return variable.dNumType;
    } else if (variable.vartype === "VT_STRING") {
        return variable.sString;
    } else if (variable.vartype === "VT_DATETIME") {
        return variable.dtDateTime;
    } else if (variable.vartype === "VT_NUMBERARRAY") {
        return JSON.stringify(variable.VarArray);
    } else if (variable.vartype === "VT_STRINGARRAY") {
        return JSON.stringify(variable.VarArray);
    } else if (variable.vartype === "VT_DATETIMEARRAY") {
        return JSON.stringify(variable.VarArray);
    }
    return undefined;
}

interface TextValue {
    value: string;
    valid: boolean;
}

function textForVariable(variable: Variable): TextValue | TextValue[] {
    if (variable.vartype === "VT_NUMBER") {
        return { value: "" + variable.dNumType, valid: true };
    } else if (variable.vartype === "VT_STRING") {
        return { value: variable.sString ?? "", valid: true };
    } else if (variable.vartype === "VT_NUMBERARRAY" || variable.vartype === "VT_STRINGARRAY") {
        const text: TextValue[] = [];
        for (const item of variable.VarArray) {
            text.push({ value: JSON.stringify(item), valid: true });
        }
        return text;
    } else if (variable.vartype === "VT_DATETIME") {
        return { value: variable.dtDateTime ?? "", valid: true };
    } else if (variable.vartype === "VT_DATETIMEARRAY") {
        const text: TextValue[] = [];
        for (const item of variable.VarArray) {
            text.push({ value: JSON.stringify(item), valid: true });
        }
        return text;
    }
    return { value: "", valid: true };
}

function setValueForVariable(variable: Variable, value: string, index?: number): boolean {
    if (value.trim().length === 0) return false;

    try {
        if (variable.vartype === "VT_NUMBER") {
            const result = parseFloat(value);
            if (isNaN(result)) return false;
            variable.dNumType = result;
        } else if (variable.vartype === "VT_STRING") {
            variable.sString = value;
        } else if (variable.vartype === "VT_DATETIME") {
            variable.dtDateTime = value;
        } else if (variable.vartype === "VT_NUMBERARRAY" && index !== undefined) {
            if (!variable.VarArray) return false;
            if (Array.isArray(variable.VarArray[index])) {
                const result = JSON.parse(value);
                if (!Array.isArray(result)) return false;
                if (result.find((v: any) => typeof v !== "number") !== undefined) {
                    return false;
                }
                variable.VarArray[index] = result;
            } else {
                variable.VarArray[index] = parseFloat(value) as any;
            }
        } else if (variable.vartype === "VT_STRINGARRAY" && index !== undefined) {
            if (!variable.VarArray) return false;
            if (Array.isArray(variable.VarArray[index])) {
                const result = JSON.parse(value);
                if (!Array.isArray(result)) return false;
                if (result.find((v: any) => typeof v !== "string") !== undefined) {
                    return false;
                }
                variable.VarArray[index] = result;
            } else {
                variable.VarArray[index] = value as any;
            }
        } else if (variable.vartype === "VT_DATETIMEARRAY" && index !== undefined) {
            if (!variable.VarArray) return false;
            variable.VarArray[index] = value as any;
        } else {
            return false;
        }
    } catch (err) {
        console.warn(err);
        return false;
    }

    return true;
}

function variableScalar(variable: Variable): boolean {
    if (variable.vartype === "VT_NUMBER") {
        return true;
    } else if (variable.vartype === "VT_STRING") {
        return true;
    } else if (variable.vartype === "VT_DATETIME") {
        return true;
    }
    return false;
}

function variableArray(variable: Variable): boolean {
    if (variable.vartype === "VT_NUMBERARRAY") {
        return true;
    } else if (variable.vartype === "VT_STRINGARRAY") {
        return true;
    } else if (variable.vartype === "VT_DATETIMEARRAY") {
        return true;
    }
    return false;
}

function isFrozenVariable(variable: Variable): boolean {
    return !!(cheatFreezes.variables[variable.varname]);
}

function isFrozenPlayerProperty(property: CustomProperty): boolean {
    return !!(cheatFreezes.playerProperties[property.Name]);
}

interface GameVariablesProps {
    variables: Variable[];
    filter?: string;
}

interface GameVariablesState {
    filter?: string;
}

class GameVariables extends ReactComponent<GameVariablesProps, GameVariablesState> {
    render() {
        const tableRows = this.props.variables.filter((variable: Variable) => {
            if (this.props.filter) {
                return variable.varname.toLowerCase().includes(this.props.filter.toLowerCase());
            } else {
                return true;
            }
        }).map((variable: Variable) => {
            return React.createElement(GameVariable, { variable: variable, key: variable.varname });
        });

        if (tableRows.length === 0) {
            return React.createElement('div', { style: { margin: '30px 0' } }, 'No Game Variables match filter conditions.');
        }

        return React.createElement('div', { className: 'cheat-menu-variables' },
            React.createElement('h2', { className: 'cheat-menu-subtitle' }, 'Game Variables'),
            React.createElement('table', null,
                React.createElement('thead', null,
                    React.createElement('tr', null,
                        React.createElement('th', null, 'Name'),
                        React.createElement('th', null, 'Value'),
                        React.createElement('th', null, 'Desc'),
                        React.createElement('th', null, 'Freeze'),
                        React.createElement('th', null, 'Properties')
                    )
                ),
                React.createElement('tbody', { className: 'cheat-menu-variables-body' }, tableRows)
            )
        );
    }
}

interface GameVariableProps {
    variable: Variable;
}

interface GameVariableState {
    value: string | number | undefined;
    text: TextValue | TextValue[];
    frozen: boolean;
}

class GameVariable extends ReactComponent<GameVariableProps, GameVariableState> {
    constructor(props: GameVariableProps) {
        super(props);
        this.state = {
            value: valueForVariable(this.props.variable),
            text: textForVariable(this.props.variable),
            frozen: isFrozenVariable(this.props.variable)
        };

        this.freezeClicked = this.freezeClicked.bind(this);
        this.inputChanged = this.inputChanged.bind(this);
        this.openVariablePropertiesDialog = this.openVariablePropertiesDialog.bind(this);
    }

    static getDerivedStateFromProps(props: GameVariableProps, state: GameVariableState): Partial<GameVariableState> | null {
        // Update state if variable value has changed in game
        const value = valueForVariable(props.variable);
        if (value !== state.value) {
            return {
                value: value,
                text: textForVariable(props.variable)
            };
        }
        return null;
    }

    freezeClicked(): void {
        const variable = this.props.variable;
        if (isFrozenVariable(variable)) {
            delete cheatFreezes.variables[variable.varname];
        } else {
            cheatFreezes.variables[variable.varname] = true;
        }
        this.setState({ frozen: isFrozenVariable(variable) });
    }

    inputChanged(index?: number): (e: ChangeEvent<HTMLInputElement>) => void {
        return (e: ChangeEvent<HTMLInputElement>) => {
            const variable = this.props.variable;
            if (setValueForVariable(variable, e.target.value, index)) {
                this.setState({
                    value: valueForVariable(variable),
                    text: textForVariable(variable),
                });
            } else {
                // Do not erase whatever the user is writing
                if (index !== undefined) {
                    const text = textForVariable(variable) as TextValue[];
                    text[index] = { value: e.target.value, valid: false };
                    this.setState({ text });
                } else {
                    this.setState({ text: { value: e.target.value, valid: false } });
                }
            }
        };
    }

    openVariablePropertiesDialog(): void {
        const dialogRoot = document.getElementById('properties-dialog-root');
        if (!dialogRoot) return;

        const ReactDOM = (window as any).ReactDOM;
        const PropertiesDialog = (window as any).PropertiesDialog;
        if (!ReactDOM || !PropertiesDialog) return;

        const variable = this.props.variable;
        const entityId = `variable:${variable.varname}`;
        const properties = variable.CustomProperties || [];

        const handleClose = () => {
            if (ReactDOM.createRoot) {
                const root = (window as any).propertiesDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).propertiesDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).propertiesDialogRoot = root;
            }
            root.render(React.createElement(PropertiesDialog, {
                entityType: 'variable',
                entityId: entityId,
                entityName: variable.varname,
                properties: properties,
                onSave: handleClose,
                onCancel: handleClose
            }));
        } else {
            ReactDOM.render(
                React.createElement(PropertiesDialog, {
                    entityType: 'variable',
                    entityId: entityId,
                    entityName: variable.varname,
                    properties: properties,
                    onSave: handleClose,
                    onCancel: handleClose
                }),
                dialogRoot
            );
        }
    }

    render() {
        const variable = this.props.variable;
        let value: any;
        if (variableScalar(variable)) {
            const text = this.state.text as TextValue;
            value = React.createElement('input', {
                onChange: this.inputChanged(),
                value: text.value,
                className: text.valid ? '' : 'invalid-value'
            });
        } else if (variableArray(variable)) {
            value = variable.VarArray?.map((arrayItem: any, idx: number) => {
                const text = this.state.text as TextValue[];
                const itemText = text[idx] || { value: String(arrayItem), valid: true };
                return React.createElement('div', { key: idx },
                    idx + ': ',
                    React.createElement('input', {
                        onChange: this.inputChanged(idx),
                        value: itemText.value,
                        className: itemText.valid ? '' : 'invalid-value'
                    })
                );
            });
        } else {
            value = "Uneditable type: " + variable.vartype;
        }

        return React.createElement('tr', null,
            React.createElement('td', null, variable.varname),
            React.createElement('td', null, value),
            React.createElement('td', { className: 'desc-cell', dangerouslySetInnerHTML: { __html: variable.VarComment || '' } }),
            React.createElement('td', null,
                React.createElement('button', {
                    onClick: this.freezeClicked,
                    className: this.state.frozen ? 'freeze-button frozen' : 'freeze-button'
                }, 'Freeze')
            ),
            React.createElement('td', null,
                React.createElement('button', { onClick: this.openVariablePropertiesDialog }, 'Properties')
            )
        );
    }
}

interface GameCustomPropertiesProps {
    properties: CustomProperty[];
    filter?: string;
}

export class GameCustomProperties extends ReactComponent<GameCustomPropertiesProps> {
    render(): any {
        let totalCount = 0;
        const tableRows = this.props.properties.filter((property: CustomProperty) => {
            ++totalCount;
            if (this.props.filter) {
                return property.Name.toLowerCase().includes(this.props.filter.toLowerCase());
            } else {
                return true;
            }
        }).map((property: CustomProperty) => {
            return React.createElement(GameCustomProperty, { property: property, key: property.Name });
        });

        if (tableRows.length === 0) {
            if (totalCount === 0) {
                return null;
            } else {
                return React.createElement('div', { style: { margin: '30px 0' } }, 'No Player Properties match filter conditions.');
            }
        }

        return React.createElement('div', { className: 'cheat-menu-player-properties' },
            React.createElement('h2', { className: 'cheat-menu-subtitle' }, 'Player Properties'),
            React.createElement('table', null,
                React.createElement('thead', null,
                    React.createElement('tr', null,
                        React.createElement('th', null, 'Name'),
                        React.createElement('th', null, 'Value'),
                        React.createElement('th', null, 'Freeze')
                    )
                ),
                React.createElement('tbody', { className: 'cheat-menu-player-properties-body' }, tableRows)
            )
        );
    }
}

interface GameCustomPropertyProps {
    property: CustomProperty;
}

interface GameCustomPropertyState {
    value: string;
    frozen: boolean;
}

class GameCustomProperty extends ReactComponent<GameCustomPropertyProps, GameCustomPropertyState> {
    constructor(props: GameCustomPropertyProps) {
        super(props);
        this.state = {
            value: this.props.property.Value,
            frozen: isFrozenPlayerProperty(this.props.property)
        };

        this.freezeClicked = this.freezeClicked.bind(this);
        this.inputChanged = this.inputChanged.bind(this);
    }

    static getDerivedStateFromProps(props: GameCustomPropertyProps, state: GameCustomPropertyState): Partial<GameCustomPropertyState> | null {
        // Update state if property value has changed in game
        if (props.property.Value !== state.value) {
            return {
                value: props.property.Value
            };
        }
        return null;
    }

    freezeClicked(): void {
        const property = this.props.property;
        if (isFrozenPlayerProperty(property)) {
            delete cheatFreezes.playerProperties[property.Name];
        } else {
            cheatFreezes.playerProperties[property.Name] = true;
        }
        this.setState({ frozen: isFrozenPlayerProperty(property) });
    }

    inputChanged(): (e: ChangeEvent<HTMLInputElement>) => void {
        return (e: ChangeEvent<HTMLInputElement>) => {
            this.props.property.Value = e.target.value;
            this.setState({ value: e.target.value });
        };
    }

    render(): any {
        const property = this.props.property;
        return React.createElement('tr', null,
            React.createElement('td', null, property.Name),
            React.createElement('td', null,
                React.createElement('input', {
                    onChange: this.inputChanged(),
                    value: this.state.value
                })
            ),
            React.createElement('td', null,
                React.createElement('button', {
                    onClick: this.freezeClicked,
                    className: this.state.frozen ? 'freeze-button frozen' : 'freeze-button'
                }, 'Freeze')
            )
        );
    }
}

interface CheatMenuContentState {
    filter: string;
}

class CheatMenuContent extends ReactComponent<{}, CheatMenuContentState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            filter: '',
        };

        this.filterChanged = this.filterChanged.bind(this);
        this.finishClicked = this.finishClicked.bind(this);
    }

    filterChanged(e: ChangeEvent<HTMLInputElement>): void {
        this.setState({ filter: e.target.value });
    }

    finishClicked(): void {
        // Close the dialog by clearing the React root
        const ReactDOM = (window as any).ReactDOM;
        const dialogRoot = document.getElementById('cheat-dialog-root');
        if (dialogRoot && ReactDOM) {
            if (ReactDOM.createRoot) {
                const cheatMenuRoot = (window as any).cheatMenuRoot;
                if (cheatMenuRoot) {
                    cheatMenuRoot.render(null);
                    // Don't destroy the root, just clear it so it can be reused
                }
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        }
    }

    render() {
        if (!TheGame) return null;

        const DialogComponent = (window as any).Dialog;
        const CheatMenuTabs = (window as any).CheatMenuTabs;
        
        if (!DialogComponent) {
            // Fallback to old structure if Dialog not available
            return React.createElement('div', { className: 'cheat-menu-content' },
                React.createElement('div', { className: 'cheat-menu-title' },
                    React.createElement('h1', null, 'Cheat Menu'),
                    React.createElement('input', { placeholder: 'Filter', value: this.state.filter, onChange: this.filterChanged })
                ),
                React.createElement('div', { className: 'cheat-menu-scrollable' },
                    React.createElement(GameCustomProperties, { properties: TheGame.Player.CustomProperties, filter: this.state.filter }),
                    React.createElement(GameVariables, { variables: TheGame.Variables, filter: this.state.filter })
                ),
                React.createElement('div', { className: 'cheat-menu-actions' },
                    React.createElement('button', { onClick: this.finishClicked }, 'Finish')
                )
            );
        }

        const handleClose = () => {
            this.finishClicked();
        };

        // Use new tabbed interface if available, otherwise fallback to old
        const content = CheatMenuTabs 
            ? React.createElement(CheatMenuTabs)
            : React.createElement('div', { style: { marginBottom: '16px' } },
                React.createElement('input', {
                    type: 'text',
                    placeholder: 'Filter',
                    value: this.state.filter,
                    onChange: this.filterChanged,
                    style: {
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        fontFamily: 'var(--font-game-text)'
                    }
                }),
                React.createElement('div', { className: 'cheat-menu-scrollable' },
                    React.createElement(GameCustomProperties, { properties: TheGame.Player.CustomProperties, filter: this.state.filter }),
                    React.createElement(GameVariables, { variables: TheGame.Variables, filter: this.state.filter })
                )
            );

        return React.createElement(DialogComponent, {
            title: 'Cheat Menu',
            onClose: handleClose,
            closeOnBackdropClick: true, // No action buttons, so close on backdrop click
            className: 'cheat-menu-dialog' // Custom class for fixed height
        }, content);
    }
}

// Make available globally
(window as any).CheatMenuContent = CheatMenuContent;
